import React, { useMemo, useState } from 'react';

import type { AtendimentoExterno, OficinaExterna } from '../../../api/oficinaExternaApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { useOficinaExternaOffline } from '../../../hooks/useOficinaExternaOffline';
import {
  enfileirar,
  guardarFoto,
  novoClienteId,
  salvarAtendimentoLocal,
} from '../../../infrastructure/offline/oficinaExternaDb';
import { notify } from '../../../shared/feedback/notify';
import SearchIcon from '../../../img/search-icon.svg';
import AtendimentoExternoModal from '../components/AtendimentoExternoModal';
import BarraDeSincronizacao from '../components/BarraDeSincronizacao';
import CadastroForm from '../components/CadastroForm';
import CarimboDeDados from '../components/CarimboDeDados';
import MinhasVisitas from '../components/MinhasVisitas';
import SeloDeEnvio from '../components/SeloDeEnvio';
import SaidaModal from '../components/SaidaModal';
import VisitaForm, { type DadosDaVisita, type VideoDaVisita } from '../components/VisitaForm';
import { dataCurta, diasSemVisitaTexto, plural, seloDeAtraso } from '../formatacao';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../recepcao/pages/Recepcao.css';
// Os botões são os do sistema (`admin-btn`), por import — a classe própria com paleta azul que
// existia aqui foi removida em 25/09/2026 (ver o comentário em `OficinaExterna.css`).
import '../../admin-sistema/pages/css/adminSistema.css';
import './OficinaExterna.css';

/**
 * De onde o inspetor saiu ao abrir um formulário — é o que o "Voltar" do topo restaura
 * (28/09/2026, pedido do Victor: "um botão Voltar que saiba voltar para onde o usuário estava").
 *
 * ⚠️ Guarda o estado ANTERIOR, não o "mais útil": aba, nó aberto e a rolagem exatos de quando ele
 * tocou no botão. Abrir por conta própria a oficina do formulário mexeria na altura do que está
 * acima e a rolagem restaurada cairia noutro lugar — o inspetor voltaria para um ponto que ele
 * nunca viu.
 */
type Origem = {
  aba: Aba;
  regiaoId: string | null;
  oficinaId: string | null;
  scrollY: number;
};

type Aba = 'OFICINAS' | 'MINHAS_VISITAS';

type Modo =
  | { tipo: 'ARVORE' }
  | { tipo: 'CADASTRO'; oficina: OficinaExterna; origem: Origem }
  | { tipo: 'VISITA'; atendimentoRef: string; placa: string; origem: Origem };

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'As duas abas desta tela',
    conteudo: (
      <>
        <p>
          <strong>Oficinas</strong> é a árvore Região → Oficina → Placa: é por ela que você
          cadastra um veículo, registra a visita e registra a saída.
        </p>
        <p>
          <strong>Minhas visitas</strong> é o histórico do que <strong>você</strong> registrou, da
          mais recente para a mais antiga, com a etapa informada, as previsões, as respostas do
          checklist e as suas observações. <strong>Ver veículo</strong> abre todas as visitas
          daquela placa, inclusive as de outros inspetores e as fotos.
        </p>
        <p>
          O botão <strong>Filtros</strong> recorta o histórico por <strong>período</strong> (a data
          em que você esteve na oficina, não a do envio), região, oficina, placa e situação do
          veículo. Não há resumo dos filtros na tela: para conferir ou desfazer, reabra o
          <strong> Filtros</strong> — ele sempre mostra o que está valendo.
        </p>
        <p>
          O que ainda não subiu aparece ali marcado como <strong>No aparelho</strong>. E se o
          histórico do servidor não puder ser consultado, a tela avisa — ela nunca diz que você não
          registrou nada quando não conseguiu perguntar.
        </p>
      </>
    ),
  },
  {
    titulo: 'Como esta tela funciona sem rede',
    conteudo: (
      <>
        <p>
          Tudo o que você faz aqui é <strong>gravado no próprio celular</strong> e enviado sozinho
          quando houver rede. Você pode cadastrar veículo, registrar visita, tirar fotos, gravar
          vídeos e registrar saída <strong>sem sinal nenhum</strong>.
        </p>
        <p>
          Cada <strong>vídeo</strong> pode ter até 100 MB e é o último item da visita a subir,
          porque é o mais pesado. Se o celular não tiver espaço para guardá-lo até a rede voltar,
          ele não é aceito e a tela avisa na hora — antes de você sair da oficina.
        </p>
        <p>
          Com tudo em ordem, o selo <strong>Tudo enviado</strong> fica no canto superior direito.
          Quando algo ainda está no aparelho, ou foi recusado pelo servidor, aparece uma faixa no
          topo com a quantidade e o botão <strong>Ver fila</strong>. Enquanto houver registros
          pendentes, <strong>não desinstale o aplicativo nem troque de celular</strong>: o que não
          subiu existe só ali.
        </p>
        <p>
          Abaixo do título, <strong>"Dados atualizados em"</strong> diz o contrário: quando este
          aparelho <strong>baixou</strong> a lista de veículos e o checklist. Se for de outro dia,
          o que você vê pode estar desatualizado — use <strong>Atualizar dados</strong> quando
          tiver sinal. As duas coisas são independentes: dá para não ter nada a enviar e ainda
          assim estar com uma lista velha.
        </p>
      </>
    ),
  },
  {
    titulo: 'Cadastrar um veículo na oficina',
    conteudo: (
      <>
        <p>
          Abra a oficina e toque em <strong>Cadastrar veículo</strong>. O{' '}
          <strong>número do evento é obrigatório</strong> — é ele que liga este veículo ao
          atendimento da Atual.
        </p>
        <p>
          Com rede, o botão <strong>Buscar no evento</strong> preenche placa, modelo e classe a
          partir do sistema de eventos. Sem rede, preencha à mão: o cadastro é conferido quando
          subir.
        </p>
        <p>
          Se outro inspetor já tiver cadastrado a mesma placa, o servidor recusa o seu cadastro e a
          tela da fila oferece <strong>anexar as suas visitas ao cadastro dele</strong> — nada do
          que você registrou se perde.
        </p>
      </>
    ),
  },
  {
    titulo: 'Registrar a visita',
    conteudo: (
      <>
        <p>
          Toque em <strong>Registrar visita</strong> na placa. A <strong>data da visita</strong> é o
          dia em que você esteve na oficina — mude se estiver registrando depois.
        </p>
        <p>
          A <strong>etapa</strong> e as <strong>duas previsões</strong> (da etapa atual e da
          entrega total) são o que a oficina informar; registre como eles disseram. As perguntas do checklist são definidas pela administração e
          podem mudar de tempos em tempos.
        </p>
        <p>
          A visita <strong>não pode ser editada nem apagada</strong> depois de salva. Se algo sair
          errado, registre uma visita nova — o histórico guarda as duas, com a data e o seu nome.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que significa "Nunca visitado"',
    conteudo: (
      <>
        O veículo foi cadastrado na oficina e <strong>ninguém registrou visita nele ainda</strong> —
        é diferente de "visitado hoje". Esses aparecem sinalizados em vermelho, e são os primeiros
        da lista de cada oficina.
      </>
    ),
  },
];

/**
 * A árvore Região → Oficina → Placa (at-114, ADR 0059): a tela inicial do INSPETOR, no lugar da
 * árvore de diretórios do Samsung Notes.
 *
 * 🔴 Nada aqui chama o servidor no momento da ação: cadastro, visita, fotos e saída vão para a
 * FILA local, e o toast diz "gravado no aparelho" — nunca "enviado" (regra da at-60). Quem sobe é
 * o hook, quando houver rede.
 */
const OficinaExternaArvorePage: React.FC = () => {
  const offline = useOficinaExternaOffline();
  const [modo, setModo] = useState<Modo>({ tipo: 'ARVORE' });
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [regiaoAberta, setRegiaoAberta] = useState<string | null>(null);
  const [oficinaAberta, setOficinaAberta] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [saidaDe, setSaidaDe] = useState<{ ref: string; placa: string } | null>(null);
  /**
   * As duas abas da tela (25/09/2026). "Oficinas" é a árvore de sempre; "Minhas visitas" é o
   * histórico do próprio inspetor, que até aqui não existia em lugar nenhum — ele registrava e
   * não tinha como reler.
   */
  const [aba, setAba] = useState<Aba>('OFICINAS');
  const [veiculoAberto, setVeiculoAberto] = useState<{ id: string; placa: string } | null>(null);

  /** Fotografia do lugar em que o inspetor está, tirada ao abrir um formulário. */
  const origemAtual = (): Origem => ({
    aba,
    regiaoId: regiaoAberta,
    oficinaId: oficinaAberta,
    scrollY: typeof window === 'undefined' ? 0 : window.scrollY,
  });

  /**
   * O "Voltar" do topo dos formulários: devolve a tela ao estado exato de onde ele saiu.
   *
   * A rolagem é restaurada no quadro seguinte porque o React ainda não repintou a árvore quando
   * este handler roda — `scrollTo` antes disso cairia numa página de uma tela de altura.
   */
  const voltarPara = (origem: Origem) => {
    setModo({ tipo: 'ARVORE' });
    setAba(origem.aba);
    setRegiaoAberta(origem.regiaoId);
    setOficinaAberta(origem.oficinaId);
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => window.scrollTo(0, origem.scrollY));
    }
  };

  const catalogo = offline.catalogo;

  /** Os veículos do servidor MAIS os cadastrados offline que ainda não subiram. */
  const atendimentos = useMemo(() => {
    const doServidor = catalogo?.atendimentos ?? [];
    const locais = offline.atendimentosLocais
      .filter((local) => !local.idServidor)
      .map(
        (local) =>
          ({
            id: local.clienteId,
            placa: local.placa,
            classe: local.classe,
            modelo: local.modelo ?? null,
            entradaEm: local.entradaEm,
            saidaEm: null,
            previsaoEntrega: null,
            previsaoEtapa: null,
            ultimaVisitaEm: null,
            diasSemVisita: null,
            evento: { id: '', eventNumber: local.eventNumber },
            oficina: {
              id: local.oficinaId,
              nome:
                catalogo?.oficinas.find((o) => o.id === local.oficinaId)?.nome ?? 'Oficina',
              regiao: { id: '', nome: '' },
            },
            etapaAtual: null,
          }) as AtendimentoExterno & { pendente?: boolean },
      )
      .map((a) => ({ ...a, pendente: true }));

    return [...doServidor, ...locais];
  }, [catalogo, offline.atendimentosLocais]);

  const termoDaBusca = busca.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  /**
   * Com termo digitado, a árvore ABRE sozinha até a placa: só regiões e oficinas com resultado
   * aparecem, já expandidas. Sem isso a busca filtrava e nada mudava na tela — os nós continuavam
   * recolhidos e a placa procurada ficava escondida atrás de dois cliques (achado do Victor,
   * 25/09/2026, buscando RKA7C9).
   */
  const buscando = termoDaBusca.length > 0;
  const filtrados = useMemo(() => {
    if (!termoDaBusca) return atendimentos;
    return atendimentos.filter((a) => a.placa.includes(termoDaBusca));
  }, [atendimentos, termoDaBusca]);

  const porOficina = useMemo(() => {
    const mapa = new Map<string, typeof filtrados>();
    for (const a of filtrados) {
      const lista = mapa.get(a.oficina.id) ?? [];
      lista.push(a);
      mapa.set(a.oficina.id, lista);
    }
    return mapa;
  }, [filtrados]);

  const diasAlerta = catalogo?.diasSemVisitaAlerta ?? 3;

  const avisarArmazenamento = (erro: unknown) => {
    notify.error(
      erro instanceof Error && erro.name === 'ArmazenamentoLocalIndisponivel'
        ? erro.message
        : 'Não foi possível gravar no aparelho. Não feche a tela antes de a rede voltar.',
      { toastId: 'oe-armazenamento' },
    );
  };

  const salvarVisita = async (
    atendimentoRef: string,
    placa: string,
    dados: DadosDaVisita,
    fotos: Blob[],
    videos: VideoDaVisita[] = [],
  ) => {
    const clienteIdDaVisita = novoClienteId();

    try {
      await enfileirar({
        clienteId: clienteIdDaVisita,
        tipo: 'VISITA',
        atendimentoRef,
        placa,
        payload: {
          clienteId: clienteIdDaVisita,
          dataVisita: dados.dataVisita,
          etapaId: dados.etapaId || undefined,
          previsaoEntrega: dados.previsaoEntrega || undefined,
          previsaoEtapa: dados.previsaoEtapa || undefined,
          observacao: dados.observacao || undefined,
          templateVersao: catalogo?.checklist.versao ?? 'vazio',
          respostas: Object.entries(dados.respostas)
            .filter(([, valor]) => valor !== undefined && valor !== '')
            .map(([itemId, valor]) => ({ itemId, valor })),
        },
        estado: 'PENDENTE',
        tentativas: 0,
        criadoEm: new Date().toISOString(),
      });

      for (const blob of fotos) {
        const clienteIdDaFoto = novoClienteId();
        await guardarFoto({
          clienteId: clienteIdDaFoto,
          visitaRef: clienteIdDaVisita,
          blob,
          mime: blob.type || 'image/jpeg',
          nome: `${clienteIdDaFoto}.jpg`,
          criadoEm: new Date().toISOString(),
        });
        await enfileirar({
          clienteId: clienteIdDaFoto,
          tipo: 'FOTO',
          // A foto é anexada à VISITA: a referência é reescrita para o id do servidor quando a
          // visita sobe (`reescreverReferencias` na fila).
          atendimentoRef: clienteIdDaVisita,
          visitaRef: clienteIdDaVisita,
          placa,
          payload: {},
          estado: 'PENDENTE',
          tentativas: 0,
          criadoEm: new Date().toISOString(),
        });
      }

      // at-132: o vídeo usa o mesmo store das fotos (o `mime` diz o que é) e um item `VIDEO`,
      // que a fila manda DEPOIS das fotos da mesma visita.
      for (const video of videos) {
        const clienteIdDoVideo = novoClienteId();
        const extensao = (video.nome.match(/\.[a-z0-9]+$/i)?.[0] ?? '.mp4').toLowerCase();
        await guardarFoto({
          clienteId: clienteIdDoVideo,
          visitaRef: clienteIdDaVisita,
          blob: video.blob,
          mime: video.blob.type || 'video/mp4',
          nome: `${clienteIdDoVideo}${extensao}`,
          criadoEm: new Date().toISOString(),
        });
        await enfileirar({
          clienteId: clienteIdDoVideo,
          tipo: 'VIDEO',
          atendimentoRef: clienteIdDaVisita,
          visitaRef: clienteIdDaVisita,
          placa,
          payload: {},
          estado: 'PENDENTE',
          tentativas: 0,
          criadoEm: new Date().toISOString(),
        });
      }

      setModo({ tipo: 'ARVORE' });
      await offline.recarregarFila();
      // 🔴 "gravada no aparelho", NUNCA "enviada": nada foi ao servidor ainda.
      const anexos = [
        fotos.length > 0 ? `${fotos.length} foto(s)` : null,
        videos.length > 0 ? `${videos.length} vídeo(s)` : null,
      ].filter(Boolean);
      notify.success(
        anexos.length > 0
          ? `Visita de ${placa} gravada no aparelho, com ${anexos.join(' e ')}. Será enviada quando houver rede.`
          : `Visita de ${placa} gravada no aparelho. Será enviada quando houver rede.`,
      );
      void offline.sincronizar();
    } catch (erro) {
      avisarArmazenamento(erro);
    }
  };

  const confirmarSaida = async (dados: { saidaEm: string; motivo?: string }) => {
    if (!saidaDe) return;
    const clienteId = novoClienteId();

    try {
      await enfileirar({
        clienteId,
        tipo: 'SAIDA',
        atendimentoRef: saidaDe.ref,
        placa: saidaDe.placa,
        payload: { clienteId, ...dados },
        estado: 'PENDENTE',
        tentativas: 0,
        criadoEm: new Date().toISOString(),
      });

      setSaidaDe(null);
      await offline.recarregarFila();
      notify.success(
        `Saída de ${saidaDe.placa} gravada no aparelho. Será enviada quando houver rede.`,
      );
      void offline.sincronizar();
    } catch (erro) {
      avisarArmazenamento(erro);
    }
  };

  if (modo.tipo === 'CADASTRO' && catalogo) {
    return (
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          {/* O "Voltar" fica no TOPO, antes do título: no celular o rodapé do formulário só
              aparece depois de rolar a tela inteira, e desistir do cadastro não pode custar isso.
              O "Cancelar" do rodapé continua existindo e faz exatamente o mesmo. */}
          <button
            type="button"
            className="admin-btn admin-btn-secondary oe-voltar"
            onClick={() => voltarPara(modo.origem)}
          >
            <span aria-hidden="true">&#8249;</span> Voltar
          </button>
          <div className="recepcao-title">Cadastrar veículo</div>
        </div>

        <BarraDeSincronizacao
          pendentes={offline.pendentes}
          recusados={offline.recusados}
          online={offline.online}
        />
        <CadastroForm
          oficina={modo.oficina}
          online={offline.online}
          placasJaNaArvore={atendimentos.filter((a) => !a.saidaEm).map((a) => a.placa)}
          onCancelar={() => voltarPara(modo.origem)}
          onSalvar={async (dados) => {
            const clienteId = novoClienteId();
            try {
              await salvarAtendimentoLocal({
                clienteId,
                placa: dados.placa,
                oficinaId: modo.oficina.id,
                eventNumber: dados.eventNumber,
                classe: dados.classe,
                modelo: dados.modelo,
                entradaEm: dados.entradaEm,
                criadoEm: new Date().toISOString(),
              });
              await enfileirar({
                clienteId,
                tipo: 'CADASTRO',
                atendimentoRef: clienteId,
                placa: dados.placa,
                payload: { clienteId, ...dados, oficinaId: modo.oficina.id },
                estado: 'PENDENTE',
                tentativas: 0,
                criadoEm: new Date().toISOString(),
              });

              setModo({ tipo: 'ARVORE' });
              setOficinaAberta(modo.oficina.id);
              await offline.recarregarFila();
              notify.success(
                `${dados.placa} gravada no aparelho. Será enviada quando houver rede.`,
              );
              void offline.sincronizar();
            } catch (erro) {
              avisarArmazenamento(erro);
            }
          }}
        />
      </div>
    );
  }

  if (modo.tipo === 'VISITA' && catalogo) {
    return (
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <button
            type="button"
            className="admin-btn admin-btn-secondary oe-voltar"
            onClick={() => voltarPara(modo.origem)}
          >
            <span aria-hidden="true">&#8249;</span> Voltar
          </button>
          <div className="recepcao-title">Registrar visita</div>
        </div>

        <BarraDeSincronizacao
          pendentes={offline.pendentes}
          recusados={offline.recusados}
          online={offline.online}
        />
        <VisitaForm
          atendimentoRef={modo.atendimentoRef}
          placa={modo.placa}
          checklist={catalogo.checklist}
          etapas={catalogo.etapas}
          onCancelar={() => voltarPara(modo.origem)}
          onSalvar={(dados, fotos, videos) =>
            salvarVisita(modo.atendimentoRef, modo.placa, dados, fotos, videos)
          }
        />
      </div>
    );
  }

  return (
    <div className="ConteDo recepcao-conte-do">
      {/* 🔴 O título vem SEMPRE primeiro, como em toda tela do sistema (regra do Victor,
          25/09/2026). A barra de sincronização nascia acima dele e empurrava o cabeçalho para
          baixo — quem trocava de tela via o título saltar de lugar. */}
      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Minhas Visitas
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>

        {/* ⚠️ A busca do cabeçalho filtra a ÁRVORE, e por isso só existe na aba dela: a aba
            "Minhas visitas" tem busca própria (que vai ao servidor, com "Buscar"), e duas caixas
            na mesma tela fariam o inspetor digitar na errada. */}
        {aba === 'OFICINAS' && (
          <div className="recepcao-search-bar">
            <div className="ItemSearch">
              <img
                className="imgIcon"
                src={SearchIcon}
                alt="Ícone de busca"
                width="20"
                height="20"
              />
            </div>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar placa"
              aria-label="Buscar placa"
              className="recepcao-search-input"
            />
          </div>
        )}

        {/* Canto superior direito do cabeçalho (25/09/2026): o selo do estado normal da fila e o
            carimbo do download. `margin-left: auto` é o que empurra — o mesmo recurso que a
            barra de sincronização usa no botão dela. */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <SeloDeEnvio
            pendentes={offline.pendentes}
            recusados={offline.recusados}
            online={offline.online}
          />
          <CarimboDeDados baixadoEm={offline.baixadoEm} origem={offline.origem} />
        </div>
      </div>

      {/* Abas do sistema (`recepcao-tabs`), o mesmo casco da Recepção e do Chefe. */}
      <div className="recepcao-tabs">
        <button
          type="button"
          className={`recepcao-tab${aba === 'OFICINAS' ? ' active' : ''}`}
          onClick={() => setAba('OFICINAS')}
        >
          Oficinas
        </button>
        <button
          type="button"
          className={`recepcao-tab${aba === 'MINHAS_VISITAS' ? ' active' : ''}`}
          onClick={() => setAba('MINHAS_VISITAS')}
        >
          Minhas visitas
        </button>
      </div>

      <BarraDeSincronizacao
        pendentes={offline.pendentes}
        recusados={offline.recusados}
        online={offline.online}
      />

      {/* "Atualizar dados" na posição do "+ Adicionar veículo" da Recepção (decisão do Victor):
          barra própria abaixo do cabeçalho, botão à direita. ⚠️ `justifyContent: flex-end` é
          preciso porque lá a barra tem DOIS filhos (filtros à esquerda, botão à direita) e o
          `space-between` da classe basta; aqui só há o botão, que sem isto iria para a esquerda. */}
      <div className="VisualizarOrdenar" style={{ justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="CtaSmall MouseHover"
          style={{
            border: 'none',
            opacity: !offline.online || offline.carregando ? 0.55 : 1,
            cursor: !offline.online || offline.carregando ? 'not-allowed' : 'pointer',
          }}
          disabled={!offline.online || offline.carregando}
          title={
            offline.online
              ? 'Baixa a lista de veículos e o checklist mais recentes'
              : 'Sem rede: os dados serão atualizados quando houver sinal'
          }
          onClick={() => void offline.atualizarCatalogo()}
        >
          <span className="ComponentName">
            {offline.carregando ? 'Atualizando...' : 'Atualizar dados'}
          </span>
        </button>
      </div>

      {aba === 'MINHAS_VISITAS' && (
        <MinhasVisitas
          fila={offline.fila}
          catalogo={catalogo}
          onAbrirVeiculo={(id, placa) => setVeiculoAberto({ id, placa })}
        />
      )}

      {/* 🔴 Sem catálogo nenhum, a tela DIZ que não tem dados. Árvore vazia seria lida como
          "não há nada para visitar" — a conclusão mais cara possível para quem está na rua. */}
      {aba === 'OFICINAS' && offline.semDados && (
        <div className="erro-de-tela" role="alert">
          Este aparelho ainda não tem os dados da Oficina Externa e não há rede para baixá-los.
          Isto NÃO quer dizer que não há veículos a visitar — quer dizer que a lista não chegou
          aqui. Conecte-se a uma rede e toque em "Atualizar dados".
        </div>
      )}

      {aba === 'OFICINAS' && catalogo && (
        <div className="oe-arvore">
          {catalogo.regioes.length === 0 && (
            <p className="oe-vazio">
              Nenhuma região cadastrada. Fale com a administração — as regiões e oficinas são
              cadastradas por lá.
            </p>
          )}

          {buscando && filtrados.length === 0 && (
            <p className="oe-vazio">Nenhum veículo com a placa "{termoDaBusca}" neste aparelho.</p>
          )}

          {catalogo.regioes.map((regiao) => {
            const oficinasDaRegiao = catalogo.oficinas.filter(
              (o) => o.regiaoId === regiao.id && (!buscando || (porOficina.get(o.id)?.length ?? 0) > 0),
            );
            const veiculosDaRegiao = oficinasDaRegiao.reduce(
              (soma, o) => soma + (porOficina.get(o.id)?.length ?? 0),
              0,
            );
            if (buscando && veiculosDaRegiao === 0) return null;
            const aberta = buscando || regiaoAberta === regiao.id;

            return (
              <div className="oe-no oe-no-regiao" key={regiao.id}>
                <div className="oe-no-cabecalho-linha">
                  <button
                    type="button"
                    className="oe-no-cabecalho"
                    aria-expanded={aberta}
                    onClick={() => setRegiaoAberta(aberta ? null : regiao.id)}
                  >
                    <span className="oe-no-nome">{regiao.nome}</span>
                    <span className="oe-no-meta">
                      {plural(oficinasDaRegiao.length, 'oficina', 'oficinas')} ·{' '}
                      {plural(veiculosDaRegiao, 'veículo', 'veículos')}
                    </span>
                  </button>
                </div>

                {aberta && (
                  <div className="oe-no-filhos">
                    {oficinasDaRegiao.length === 0 && (
                      <p className="oe-vazio centrado">Nenhuma oficina cadastrada nesta região.</p>
                    )}

                    {oficinasDaRegiao.map((oficina) => {
                      const veiculos = (porOficina.get(oficina.id) ?? []).slice().sort((a, b) => {
                        if (a.diasSemVisita === null && b.diasSemVisita !== null) return -1;
                        if (b.diasSemVisita === null && a.diasSemVisita !== null) return 1;
                        return (b.diasSemVisita ?? 0) - (a.diasSemVisita ?? 0);
                      });
                      const abertaOficina = buscando || oficinaAberta === oficina.id;

                      return (
                        <div className="oe-no oe-no-oficina" key={oficina.id}>
                          {/* Cadastro NA LINHA do cabeçalho, à direita — não como bloco de
                              largura inteira dentro do nó (era o grid do `oe-no-filhos`
                              esticando o botão). */}
                          <div className="oe-no-cabecalho-linha">
                            <button
                              type="button"
                              className="oe-no-cabecalho"
                              aria-expanded={abertaOficina}
                              onClick={() => setOficinaAberta(abertaOficina ? null : oficina.id)}
                            >
                              <span className="oe-no-nome">{oficina.nome}</span>
                              <span className="oe-no-meta">
                                {plural(veiculos.length, 'veículo', 'veículos')}
                              </span>
                            </button>
                            <button
                              type="button"
                              className="admin-btn admin-btn-primary"
                              onClick={() =>
                                setModo({ tipo: 'CADASTRO', oficina, origem: origemAtual() })
                              }
                            >
                              Cadastrar veículo
                            </button>
                          </div>

                          {abertaOficina && (
                            <div className="oe-no-filhos">
                              {veiculos.length === 0 && (
                                <p className="oe-vazio centrado">
                                  Nenhum veículo cadastrado nesta oficina.
                                </p>
                              )}

                              {veiculos.map((veiculo) => {
                                const selo = seloDeAtraso(veiculo.diasSemVisita, diasAlerta);
                                const pendente = (veiculo as { pendente?: boolean }).pendente;

                                return (
                                  <div className="oe-placa-card" key={veiculo.id}>
                                    <div className="oe-placa-info">
                                      <div className="oe-placa-linha">
                                        <span className="oe-placa-nome">{veiculo.placa}</span>
                                        <span
                                          className={`cilia-badge ${selo.classe}`}
                                          title={selo.titulo}
                                        >
                                          {diasSemVisitaTexto(veiculo.diasSemVisita)}
                                        </span>
                                        {pendente && (
                                          <span
                                            className="cilia-badge badge-laranja"
                                            title="Cadastro ainda no aparelho — será enviado quando houver rede."
                                          >
                                            No aparelho
                                          </span>
                                        )}
                                      </div>

                                      <div className="oe-no-meta">
                                        {veiculo.modelo || 'Modelo não informado'} · Evento{' '}
                                        {veiculo.evento?.eventNumber ?? '-'} · Entrada{' '}
                                        {dataCurta(veiculo.entradaEm)}
                                        {veiculo.etapaAtual
                                          ? ` · ${veiculo.etapaAtual.nome}`
                                          : ''}
                                        {veiculo.previsaoEtapa
                                          ? ` · Etapa até ${dataCurta(veiculo.previsaoEtapa)}`
                                          : ''}
                                        {veiculo.previsaoEntrega
                                          ? ` · Entrega ${dataCurta(veiculo.previsaoEntrega)}`
                                          : ''}
                                      </div>
                                    </div>

                                    <div className="oe-placa-acoes">
                                      <button
                                        type="button"
                                        className="admin-btn admin-btn-primary"
                                        onClick={() =>
                                          setModo({
                                            tipo: 'VISITA',
                                            atendimentoRef: veiculo.id,
                                            placa: veiculo.placa,
                                            origem: origemAtual(),
                                          })
                                        }
                                      >
                                        Registrar visita
                                      </button>
                                      <button
                                        type="button"
                                        className="admin-btn admin-btn-danger-outline"
                                        onClick={() =>
                                          setSaidaDe({ ref: veiculo.id, placa: veiculo.placa })
                                        }
                                      >
                                        Registrar saída
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {veiculoAberto && (
        <AtendimentoExternoModal
          atendimentoId={veiculoAberto.id}
          placa={veiculoAberto.placa}
          onClose={() => setVeiculoAberto(null)}
        />
      )}

      {saidaDe && (
        <SaidaModal
          placa={saidaDe.placa}
          onCancelar={() => setSaidaDe(null)}
          onConfirmar={confirmarSaida}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Minhas Visitas"
        secoes={helpSections}
      />
    </div>
  );
};

export default OficinaExternaArvorePage;
